import { unstable_noStore } from "next/cache"
import Image from "next/image"
import Link from "next/link"
import type { ReactNode } from "react"
import { wordPressApi } from "@/services/api"

interface MenuItem {
  ID?: number
  id?: number
  title?: string
  url?: string
  children?: MenuItem[]
}

interface FooterMenu {
  id: number
  name: string
  items: MenuItem[]
}

interface FooterOptions {
  acf?: {
    contactinfos?: {
      infos?: string
    }
    footerinfo?: {
      infos?: string
      paymentmethods?: {
        url?: string
      }
    }
  }
}

const FOOTER_MENU_NAMES = [
  "Footer Widget 1",
  "Footer Widget 2",
  "Footer Widget 3",
] as const

const FOOTER_TITLES: Record<(typeof FOOTER_MENU_NAMES)[number], string> = {
  "Footer Widget 1": "Ajuda e Suporte",
  "Footer Widget 2": "Sobre a Triibo",
  "Footer Widget 3": "Faça parte da Triibo",
}

const LEADING_SLASHES_REGEX = /^\/+/

async function getFooterData(): Promise<{
  menus: FooterMenu[]
  options: FooterOptions | null
}> {
  const [menusResult, optionsResult] = await Promise.allSettled([
    wordPressApi.get<FooterMenu[]>("/triibo/v1/menus"),
    wordPressApi.get<FooterOptions>("/acf/v3/options/options"),
  ])

  const menus =
    menusResult.status === "fulfilled" && Array.isArray(menusResult.value.data)
      ? menusResult.value.data
      : []

  const options =
    optionsResult.status === "fulfilled"
      ? (optionsResult.value.data ?? null)
      : null

  return {
    menus,
    options,
  }
}

function getMenuItemHref(itemUrl: string): string {
  try {
    const parsedUrl = new URL(itemUrl)

    if (parsedUrl.hostname.includes("marketplace")) {
      const uri = parsedUrl.pathname.replace(LEADING_SLASHES_REGEX, "")
      return uri ? `/${uri}` : "/"
    }

    return itemUrl
  } catch {
    return itemUrl
  }
}

function renderMenuItem(item: MenuItem): ReactNode {
  const itemId = item?.ID ?? item?.id
  const itemUrl = item?.url ?? "#"
  const itemHref = getMenuItemHref(itemUrl)
  const itemText = item?.title ?? ""
  const key = itemId ? `menu-${itemId}` : `${itemText}-${itemUrl}`
  const hasChildren = Boolean(item?.children?.length)

  return (
    <li className='text-sm' key={key}>
      <Link className='text-zinc-600 hover:text-zinc-900' href={itemHref}>
        {itemText}
      </Link>
      {hasChildren ? (
        <ul className='mt-2 space-y-2 border-zinc-200 border-l pl-3'>
          {item.children?.map((child) => renderMenuItem(child))}
        </ul>
      ) : null}
    </li>
  )
}

export async function Footer(): Promise<ReactNode> {
  unstable_noStore()

  try {
    const { menus, options } = await getFooterData()

    const footerMenus = menus.filter((menu) =>
      FOOTER_MENU_NAMES.includes(
        menu.name as (typeof FOOTER_MENU_NAMES)[number]
      )
    )

    const contactInfo = options?.acf?.contactinfos?.infos
    const footerInfo = options?.acf?.footerinfo?.infos
    const paymentUrl = options?.acf?.footerinfo?.paymentmethods?.url

    return (
      <footer className='mt-auto w-full border-zinc-200 border-t bg-white'>
        <div className='mx-auto grid w-full max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-4 lg:px-8'>
          {footerMenus.map((menu) => {
            const title = FOOTER_TITLES[menu.name as keyof typeof FOOTER_TITLES]
            return (
              <section className='space-y-3' key={menu.id}>
                <h3 className='font-semibold text-sm text-zinc-900 uppercase tracking-wide'>
                  {title ?? menu.name}
                </h3>
                <ul className='space-y-2'>
                  {menu.items?.map((item) => renderMenuItem(item))}
                </ul>
              </section>
            )
          })}

          <section className='space-y-3'>
            <h3 className='font-semibold text-sm text-zinc-900 uppercase tracking-wide'>
              Fale Conosco
            </h3>
            {contactInfo ? (
              <div
                className='prose prose-sm text-zinc-600'
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <!-- O conteúdo é controlado e formatado pela equipe de conteúdo, não por usuários finais. -->
                dangerouslySetInnerHTML={{ __html: contactInfo }}
              />
            ) : (
              <p className='text-sm text-zinc-600'>
                Não foi possível carregar os dados de contato.
              </p>
            )}
          </section>
        </div>

        <div className='border-zinc-200 border-t bg-zinc-50'>
          <div className='mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8'>
            {footerInfo ? (
              <div
                className='prose prose-sm text-zinc-600'
                // biome-ignore lint/security/noDangerouslySetInnerHtml: <!-- O conteúdo é controlado e formatado pela equipe de conteúdo, não por usuários finais. -->
                dangerouslySetInnerHTML={{ __html: footerInfo }}
              />
            ) : (
              <p className='text-sm text-zinc-600'>
                Não foi possível carregar os dados do rodapé.
              </p>
            )}

            <div className='flex items-center gap-4'>
              <span className='text-sm text-zinc-600'>
                Formas de Pagamento:
              </span>
              {paymentUrl ? (
                <Image
                  alt='Formas de Pagamento | Marketplace Triibo'
                  height={111}
                  src={paymentUrl}
                  width={264}
                />
              ) : (
                <span className='text-sm text-zinc-500'>Indisponível</span>
              )}
            </div>
          </div>
        </div>
      </footer>
    )
  } catch (error) {
    console.error("Erro ao carregar dados do Footer:", error)
    return (
      <footer className='w-full border-zinc-200 border-t bg-white'>
        <div className='mx-auto w-full max-w-6xl px-4 py-10 text-sm text-zinc-600 sm:px-6 lg:px-8'>
          Não foi possível carregar os dados do rodapé.
        </div>
      </footer>
    )
  }
}
